<template>
    <div>
        <label v-if="label" class="form-field__label">
            {{ label }}
        </label>
        <div v-clickaway="inputBlur" :class="searchAutocompleteClass">
            <div :class="iconClass">
                <common-icon :name="iconName" size="large"></common-icon>
            </div>

            <input
                v-model="searchTerm"
                type="text"
                class="search-autocomplete__input"
                :placeholder="placeholder"
                @keyup="onKeyUp"
                @keydown="onKeyDown"
                @focus="inputFocus"
            />

            <div v-if="isFocused && (items.length || showNoResults)" class="search-autocomplete__suggestions">
                <div v-if="showNoResults" class="search-autocomplete__no-results">
                    {{ $t('common.microcopy.no_results_found') }}
                </div>
                <div
                    v-for="(item, index) in items"
                    :key="index"
                    :class="{
                        'search-autocomplete__single-item--selected': isSelected(item),
                        'search-autocomplete__single-item--active': index === highlightedIndex,
                    }"
                    class="search-autocomplete__single-item"
                    @mouseenter="highlightedIndex = index"
                >
                    <div class="search-results search-results--stacked" @click="selectItem(item)">
                        <div class="search-results__name">{{ item.name }}</div>
                        <div v-if="item.testName" class="search-results__test-name">{{ item.testName }}</div>
                        <div v-if="item.matchedKeyword" class="search-results__matched-keyword">{{ item.matchedKeyword }}</div>
                    </div>
                    <div v-if="isSelected(item)" class="search-autocomplete__single-item--selected--remove">
                        <common-icon name="close" size="tiny" @click="removeItem(item)"></common-icon>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import debounce from 'lodash.debounce'
    import config from '../../../config'

    export default {
        components: {},

        props: {
            placeholder: { type: String, required: true },
            label: { type: String, required: false, default: '' },
            icon: { type: String, default: 'member' },
            iconPosition: { type: String, default: 'left' },
            iconBackground: { type: String, default: 'secondary' },
            items: { type: Array, default: () => [] },
            selectedItems: { type: Array, default: () => [] },
            isSearching: { type: Boolean, default: false },
        },

        data() {
            return {
                searchTerm: '',
                isFocused: false,
                isTyping: false,
                highlightedIndex: 0,
            }
        },

        computed: {
            searchAutocompleteClass() {
                return {
                    [`search-autocomplete__search-input--${this.iconPosition}`]: true,
                    'search-autocomplete__search-input': true,
                    'form-input-text': true,
                }
            },

            iconName() {
                return `${this.icon}`
            },

            iconClass() {
                return {
                    'search-autocomplete__icon': true,
                    [`search-autocomplete__icon--${this.iconPosition}`]: true,
                    [`search-autocomplete__icon--color-${this.iconBackground}`]: this.isFocused,
                }
            },

            showNoResults() {
                return (
                    !this.isTyping &&
                    !this.isSearching &&
                    Array.isArray(this.items) &&
                    this.items.length === 0 &&
                    this.searchTerm.length >= 2
                )
            },
        },

        methods: {
            onKeyUp(event) {
                if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
                    return
                }
                this.isFocused = true
                this.isTyping = this.searchTerm.length >= 2
                this.inputChange()
            },

            inputChange: debounce(function () {
                this.isTyping = false
                if (this.searchTerm.length >= 2) {
                    this.emitEvent({ name: 'input-change', payload: { searchTerm: this.searchTerm } })
                } else {
                    this.emitEvent({ name: 'no-results', payload: { searchTerm: this.searchTerm } })
                }
            }, config.DEBOUNCE.DEFAULT),

            onKeyDown(event) {
                if (!this.items.length) {
                    return
                }

                if (event.key === 'ArrowDown') {
                    event.preventDefault()
                    this.highlightedIndex = (this.highlightedIndex + 1) % this.items.length
                } else if (event.key === 'ArrowUp') {
                    event.preventDefault()
                    this.highlightedIndex = (this.highlightedIndex - 1 + this.items.length) % this.items.length
                } else if (event.key === 'Enter') {
                    event.preventDefault()
                    const item = this.items[this.highlightedIndex]
                    if (item) {
                        this.selectItem(item)
                    }
                }
            },

            inputFocus() {
                this.emitEvent({ name: 'input-change', payload: { searchTerm: this.searchTerm } })
                this.isFocused = true
            },

            inputBlur() {
                this.isFocused = false
            },

            selectItem(item) {
                if (this.isSelected(item)) {
                    return false
                }
                this.emitEvent({ name: 'item-selected', payload: item })
                this.isFocused = false
                this.highlightedIndex = 0
            },

            isSelected(item) {
                return this.selectedItems.map((item) => item.id).includes(item.id)
            },

            removeItem(item) {
                this.emitEvent({ name: 'remove-item', payload: item })
            },
        },

        watch: {
            items() {
                this.highlightedIndex = 0
            },
        },
    }
</script>
